import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtINR, fmtNum, fmtDateDMY, todayISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingUp, Fuel, Wallet } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [digest, setDigest] = useState(null);
  const [flags, setFlags] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [escalations, setEscalations] = useState({ open_escalated: [], log: [] });
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(todayISO());

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [d, f, l, es] = await Promise.all([
          api.get(`/reports/daily_digest/${date}`).then((r) => r.data),
          api.get(`/flags/?status=OPEN`).then((r) => r.data),
          api.get(`/manager/debtor_ledger`).then((r) => r.data),
          api.get(`/integrations/escalations`).then((r) => r.data).catch(() => ({ open_escalated: [], log: [] })),
        ]);
        setDigest(d);
        setFlags(f);
        setLedger(l);
        setEscalations(es);
      } catch (e) {
        // silently ok
      } finally {
        setLoading(false);
      }
    })();
  }, [date]);

  const s = digest?.summary || {};

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Welcome back, <strong>{user?.name}</strong> — {fmtDateDMY(date)}</p>
        </div>
        <input
          data-testid="dashboard-date-picker"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="HSD Litres" value={fmtNum(s.hsd_litres, 2)} icon={Fuel} testId="stat-hsd" drillTo={`/drill/hsd_litres?from=${date}&to=${date}`} />
        <StatCard label="MS Litres" value={fmtNum(s.ms_litres, 2)} icon={Fuel} testId="stat-ms" drillTo={`/drill/ms_litres?from=${date}&to=${date}`} />
        <StatCard label="CNG Kg" value={fmtNum(s.cng_kg, 2)} icon={Fuel} testId="stat-cng" drillTo={`/drill/cng_kg?from=${date}&to=${date}`} />
        <StatCard label="Lubricants ₹" value={fmtINR(s.lubricants_total)} icon={Wallet} testId="stat-lub" drillTo={`/drill/lubricants_total?from=${date}&to=${date}`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard label="Total Fuels Sales" value={fmtINR(s.sales_fuels_rs)} icon={TrendingUp} testId="stat-fuel-sales" big drillTo={`/drill/sales_fuels_rs?from=${date}&to=${date}`} />
        <StatCard label="Total CNG Sales" value={fmtINR(s.sales_cng_rs)} icon={TrendingUp} testId="stat-cng-sales" big drillTo={`/drill/sales_cng_rs?from=${date}&to=${date}`} />
      </div>

      {(["admin", "manager", "assistant_manager", "owner"].includes(user?.role)) && escalations.open_escalated?.length > 0 && (
        <Card data-testid="escalations-card" className="p-4 border-2 border-red-500 bg-red-50 rounded-sm shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-600" />
              <h3 className="font-bold text-red-900 uppercase tracking-wider text-sm">
                Escalated to Owner · {escalations.open_escalated.length} open
              </h3>
            </div>
            <div className="num text-2xl font-bold text-red-700">
              {fmtINR(escalations.open_escalated.reduce((a, f) => a + (f.rupee_impact || 0), 0))}
            </div>
          </div>
          <p className="text-xs text-red-800 mb-3">
            Manager has not closed these within threshold. Total exposure above.
          </p>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {escalations.open_escalated.slice(0, 10).map((f) => (
              <Link
                key={f.flag_id}
                to="/flags"
                data-testid={`escalated-row-${f.flag_id}`}
                className="block bg-white border border-red-300 p-2 rounded-sm hover:border-red-500"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-red-800">{f.type} · {f.actor_ref}</div>
                    <div className="text-sm text-slate-900 truncate">{f.message}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{f.date} {f.shift_code} · escalated {f.escalated_at?.slice(0, 10)}</div>
                  </div>
                  <div className="num font-bold text-red-700 whitespace-nowrap">{fmtINR(f.rupee_impact)}</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}


      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4 border-2 border-slate-300 rounded-sm shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-800">Open Flags · Top by ₹</h3>
            <Link to={`/drill/flag_impact?from=${date}&to=${date}`} className="text-xs text-slate-600 underline">Drill through</Link>
          </div>
          <div className="space-y-2">
            {flags.length === 0 && <div className="text-sm text-slate-500">No open flags. Good work.</div>}
            {flags.slice(0, 5).map((f) => (
              <div key={f.flag_id} data-testid={`flag-row-${f.flag_id}`} className="flex items-start justify-between border border-slate-200 p-2 rounded-sm">
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                    <AlertTriangle size={12} className={f.severity === "HIGH" ? "text-red-600" : "text-amber-600"} />
                    {f.type}
                  </div>
                  <div className="text-sm text-slate-800 mt-1 truncate">{f.message}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{f.actor_ref} · {f.date} {f.shift_code}</div>
                </div>
                <div className="num text-red-600 font-bold whitespace-nowrap ml-2">{fmtINR(f.rupee_impact)}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 border-2 border-slate-300 rounded-sm shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-800">Missing / Unreadable Proofs</h3>
          </div>
          <div className="text-3xl font-mono font-bold text-slate-900">{digest?.missing_proofs || 0}</div>
          <div className="text-xs text-slate-600 mt-1 uppercase tracking-wider">Missing today</div>
          <div className="text-xl font-mono font-bold text-amber-700 mt-3">{digest?.unreadable_proofs || 0}</div>
          <div className="text-xs text-slate-600 mt-1 uppercase tracking-wider">Unreadable today</div>
          <Link to="/uploads" className="mt-3 inline-block">
            <Button variant="outline" size="sm" className="border-slate-300">Open Upload →</Button>
          </Link>
        </Card>

        <Card className="p-4 border-2 border-slate-300 rounded-sm shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-800">Top Outstanding Debtors</h3>
            <Link to={`/drill/debtor_total?from=${date}&to=${date}`} className="text-xs text-slate-600 underline">Drill through</Link>
          </div>
          <div className="space-y-1.5">
            {ledger.slice(0, 5).map((d) => (
              <div key={d.debtor_id} className="flex justify-between text-sm border-b border-slate-100 pb-1">
                <span className="truncate">{d.name}</span>
                <span className="num font-semibold text-slate-900">{fmtINR(d.outstanding)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testId, big, drillTo }) {
  const content = (
    <div data-testid={testId} className={"bg-white border-2 border-slate-300 p-3 rounded-sm shadow-sm " + (drillTo ? "hover:border-slate-900 hover:shadow cursor-pointer transition-colors group" : "")}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</div>
        {Icon && <Icon size={14} className="text-slate-400" />}
      </div>
      <div className={"font-mono font-bold text-slate-900 mt-1 " + (big ? "text-3xl" : "text-2xl")}>{value}</div>
      {drillTo && <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-slate-700 mt-1">Click to drill →</div>}
    </div>
  );
  return drillTo ? <Link to={drillTo} className="block">{content}</Link> : content;
}
